import { Box, Divider, Tooltip, Typography } from '@mui/material';
import { RichTreeView, TreeItem2, TreeItem2Props } from '@mui/x-tree-view';
import React, { Dispatch, RefObject, SetStateAction, SyntheticEvent } from 'react';
import LoadingIndicator from 'src/components/common/LoadingIndicator/LoadingIndicator';
import { Permissions, UserPermission, PermissionContextMapping } from '@lanterds/types';
import { SelectedPermission, DealerHierarchyItem, SelectedDealer, SelectedCustomer } from '../types';
import ClearIcon from '@mui/icons-material/Clear';
import { PermissionContextDisplayName, PermissionDisplayNames } from 'src/util/constants';

type Props = {
    selectedPermissions: SelectedPermission[];
    isUserPermissionsFetching?: boolean;
    permissionsOptions: SelectedPermission[];
    containerRef: RefObject<HTMLElement>;
    setSelectedPermissions: Dispatch<SetStateAction<SelectedPermission[]>>;
    setSelectedCustomers: Dispatch<SetStateAction<SelectedCustomer[]>>;
    setSelectedDealers: Dispatch<SetStateAction<SelectedDealer[]>>;
    setDealers: Dispatch<SetStateAction<DealerHierarchyItem[]>>;
};

export default function PermissionFlowTile({
    selectedPermissions,
    setSelectedPermissions,
    isUserPermissionsFetching,
    permissionsOptions,
    containerRef,
    setSelectedCustomers,
    setSelectedDealers,
    setDealers,
}: Props) {
    const ClearSelectionsButton = () => {
        return (
            <Typography onClick={() => setSelectedPermissions([])} className="clear-button" component="div">
                <ClearIcon className="clear-icon" /> Clear
            </Typography>
        );
    };

    const isPermissionDisabledHandler = (item: SelectedPermission) => {
        const { context } = item;

        if (!selectedPermissions.length || selectedPermissions[0].context === context) {
            return false;
        }

        return true;
    };

    const PermissionTreeItem = (props: TreeItem2Props) => {
        const { itemId } = props;
        const contextId = PermissionContextMapping[itemId as Permissions];
        let toolTipTitle = '';

        if (selectedPermissions.length && selectedPermissions[0].context !== contextId) {
            const contextName = PermissionContextDisplayName[selectedPermissions[0].context];
            toolTipTitle = `Only multiple selections of the same ${contextName} context can be made.`;
        }

        return (
            <Tooltip arrow title={toolTipTitle} placement="top-end">
                {/* Need to wrap in block element or else TreeItem2 throws a warning from the tooltip */}
                <Box>
                    <TreeItem2 {...props} />
                </Box>
            </Tooltip>
        );
    };

    const permissionSelectionHandler = (event: SyntheticEvent<Element, Event>, selectedPermissionNames: string[]) => {
        setSelectedPermissions(
            selectedPermissionNames.map((name) => ({
                displayName: PermissionDisplayNames[name as Permissions],
                name: name as keyof UserPermission,
                context: PermissionContextMapping[name as Permissions],
            })),
        );
        if (!selectedPermissions.length) {
            setSelectedCustomers([]);
            setSelectedDealers([]);
            setDealers([]);
        }
    };

    const validPermissions = permissionsOptions.filter(
        (opt): opt is SelectedPermission => typeof opt.displayName === 'string' && typeof opt.context === 'number',
    );

    return (
        <Box className="container permissions-container" ref={containerRef}>
            <Box className="header-container">
                <Typography variant="h6" component="div" className="header">
                    Permissions
                </Typography>
                {selectedPermissions.length ? <ClearSelectionsButton /> : <></>}
            </Box>
            <Divider />

            <Box className="content-container">
                {isUserPermissionsFetching ? (
                    <Box className="loading-container">
                        <LoadingIndicator />
                    </Box>
                ) : (
                    <>
                        <RichTreeView
                            multiSelect={true}
                            id="permission-tree-view"
                            getItemLabel={(item: SelectedPermission) => item.displayName}
                            getItemId={(item: SelectedPermission) => item.name}
                            items={validPermissions}
                            slots={{ item: PermissionTreeItem }}
                            onSelectedItemsChange={permissionSelectionHandler}
                            selectedItems={selectedPermissions.flatMap((permission) => permission.name)}
                            checkboxSelection={true}
                            isItemDisabled={isPermissionDisabledHandler}
                        />
                    </>
                )}
                {!permissionsOptions.length && (
                    <Typography className="no-data-message" variant="h5">
                        All permissions have been added.
                    </Typography>
                )}
            </Box>
        </Box>
    );
}
